// biome-ignore lint/correctness/noUnusedImports: existing error
import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectDefaultValue,
  selectRetailer,
  selectValue,
  UPDATE_E_GIFT_CARD_VALUE,
} from '~/common/fabric/GiftCard/reducers/eGiftCard';
import {centsToDollarDisplay} from '~/common/utils/payment_utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {MinusIcon} from '~library/svg/MinusIcon';
import {PlusIcon} from '~library/svg/PlusIcon';
import {charcoal, formBorderDisabled} from '~libSass/base/_exports.sass';

import './ValueControl.sass';

const STEP = 500;
const OPTIONS = [2500, 5000, 10000];

export const ValueControl = ({disabled}) => {
  const dispatch = useDispatch();
  const initial = useSelector(selectValue);
  const retailer = useSelector(selectRetailer);
  const defaultValue = useSelector(selectDefaultValue);
  const [value, setValue] = useState(initial);

  const onMinusClick = useCallback(() => {
    setValue(Math.max(retailer.min_value, value - STEP));
  }, [value, retailer.min_value]);

  const onPlusClick = useCallback(() => {
    setValue(Math.min(value + STEP, retailer.max_value));
  }, [value, retailer.max_value]);

  const isValueOutsideRange = useCallback(
    (val) => val > retailer.max_value || val < retailer.min_value,
    [retailer]
  );

  useEffect(() => {
    dispatch({
      type: UPDATE_E_GIFT_CARD_VALUE,
      payload: value,
    });
  }, [value, dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (disabled === true) {
      setValue(defaultValue);
    }
  }, [disabled]);

  // when retailer is changed
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    // if we are coming from a static retailer with a non-ten based value
    if (!retailer.is_static_value && value % STEP !== 0) {
      // round to the nearest $STEP (check it's within range below)
      setValue(Math.ceil((value + 99) / STEP) * STEP);
    }
    // if the card is static and the value is not the static value,
    // or we are outside the range, set the default value for the card
    if ((retailer.is_static_value && value !== retailer.min_value) || isValueOutsideRange(value)) {
      setValue(defaultValue);
    }
  }, [retailer]);

  const minValueReached = disabled || value === retailer.min_value;
  const maxValueReached = disabled || value === retailer.max_value;

  return (
    !disabled &&
    (retailer.is_static_value ? (
      <Typography
        variant="paragraph4"
        color="pebble"
        className="value-control__static-value-message"
      >
        *{retailer.product_config_name || retailer.name} only has 1 available cash value amount.
      </Typography>
    ) : (
      <div className="value-control-wrapper">
        <div className="value-control-wrapper__upper">
          <Button
            data-qa-id="input-decrease"
            variant="transparent"
            className={`input-control input-control-dec ${
              minValueReached ? 'input-control--disabled' : ''
            }`}
            onClick={onMinusClick}
            disabled={minValueReached}
          >
            <MinusIcon ratio={1.25} color={minValueReached ? formBorderDisabled : charcoal} />
          </Button>

          <span className={`input-value ${disabled ? 'disabled' : ''}`} data-qa-id="input-value">
            {disabled ? 0 : centsToDollarDisplay(value, true, true)}
          </span>

          <Button
            data-qa-id="input-increase"
            variant="transparent"
            className={`input-control input-control-inc ${
              maxValueReached ? 'input-control--disabled' : ''
            }`}
            onClick={onPlusClick}
            disabled={maxValueReached}
          >
            <PlusIcon ratio={1.25} color={maxValueReached ? formBorderDisabled : charcoal} />
          </Button>
        </div>
        <div className="value-control-wrapper__lower">
          {OPTIONS.map((option) => (
            <Button
              data-qa-id={`option-btn-${option}`}
              variant="secondary"
              size="large"
              className={`option-btn ${disabled || isValueOutsideRange(option) ? 'disabled' : ''}`}
              onClick={() => setValue(option)}
              disabled={disabled || isValueOutsideRange(option)}
              key={option}
            >
              {centsToDollarDisplay(option, true, true)}
            </Button>
          ))}
        </div>
      </div>
    ))
  );
};
